/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Landscaping Contractors",
  title: "AI for landscaping contractors",
  titleHighlight: "from bid to maintenance visit.",
  description: `One platform where AI helps landscaping contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for landscaping contractors",
    items: [
      {
        icon: Sparkles,
        title: "Plant schedule extraction",
        description:
          "Upload a landscape architect's plant schedule PDF and AI drafts species, counts, and sizes as structured line items for your review.",
      },
      {
        icon: Sparkles,
        title: "Hardscape cost matching",
        description:
          "Draw your paver and wall measurements on the blueprint, and AI matches every line to materials and labor in your cost database.",
      },
      {
        icon: Sparkles,
        title: "Maintenance contracts",
        description:
          "Recurring visit tracking, service templates, and multi-property customer records in the same workspace as your installs.",
      },
      {
        icon: Sparkles,
        title: "Seasonal cost updates",
        description:
          "Sod, plants, and mulch prices change by season. Keep your cost database fresh and every future estimate uses the latest.",
      },
      {
        icon: Sparkles,
        title: "Nursery price imports",
        description:
          "Import supplier catalogs from PDFs and spreadsheets directly into your cost database.",
      },
      {
        icon: Sparkles,
        title: "Hank for landscapers",
        description:
          "'What did we spend on pavers last quarter?' Real numbers from your own books.",
      },
    ],
  },

  workflow: {
    title: "How it works for landscaping contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Intake",
        title: "From plant schedule to structured scope",
        description:
          "Upload the plant schedule PDF and AI drafts a structured list of species, counts, and sizes for you to review. Draw hardscape measurements on the drawings. You control what's measured; AI carries the weight after.",
        details: [],
      },
      {
        label: "Bid",
        title: "AI matches every line to your nursery database",
        description:
          "Each reviewed line is matched to your nursery and labor database. Accept the match, override with an alternative, or bulk-edit similar lines. Hank audits the estimate for missing labor or scope gaps before you ship.",
        details: [],
      },
      {
        label: "Run",
        title: "Install and maintenance in one workspace",
        description:
          "Accepted proposals become projects with budget tracking and tasks. Maintenance contracts with visit frequency live alongside installs so you can see the full customer history in one place.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Built for the reality of landscaping",
      description:
        "Sod is one price in April, another in July. Nurseries change inventory weekly. Your cost database needs to move with the season. Contractor Co-Pilot's cost database supports size-based variance factors and waste factors so your estimates stay accurate when conditions shift.",
      bullets: [
        "Size-based production variance (big trees take longer)",
        "Waste factors per material type",
        "Seasonal cost updates from suppliers",
        "Pre-loaded for common landscaping materials",
      ],
    },
    {
      icon: Sparkles,
      title: "AI drafts structured data from plant schedules",
      description:
        "Landscape architects ship plant schedules as tables in PDFs. Upload the schedule and AI drafts species, counts, and sizes as structured line items for your review — no more manual transcription from 12-page drawings, but you still review every entry before it commits.",
      bullets: [
        "PDF schedule extraction with human review",
        "Species matching against your nursery database",
        "Size and caliper tracking",
        "Multi-page schedule support",
      ],
    },
    {
      icon: Sparkles,
      title: "Install and maintenance in one system",
      description:
        "Most landscaping software does installs OR service contracts. Contractor Co-Pilot does both in one workspace so a crew that plants trees in spring and mows lawns in summer can track everything in one place without double entry.",
      bullets: [
        "Unified install and service workflows",
        "Recurring visit tracking for maintenance",
        "Service route optimization",
        "Budget vs. actual per contract type",
      ],
    },
    {
      icon: Sparkles,
      title: "Hank knows your numbers",
      description:
        "Ask 'what's my average margin on commercial maintenance contracts?' and Hank answers from your actual historical data. Not generic landscaping industry averages — your real business.",
      bullets: [
        "Plain-English queries on your data",
        "Margin analysis by service type",
        "Crew performance tracking",
        "Historical cost lookups",
      ],
    },
  ],

  relatedFeatures: [
    {
      label: "Landscaping Solutions",
      href: "/solutions/landscaping",
      icon: Ruler,
    },
    {
      label: "Landscaping Calculator",
      href: "/tools/landscaping-calculator",
      icon: Calculator,
    },
    {
      label: "How to Estimate Landscaping Projects",
      href: "/blog/how-to-estimate-landscaping-projects",
      icon: BookOpen,
    },
    {
      label: "CC vs Spreadsheets",
      href: "/compare/spreadsheets",
      icon: BarChart3,
    },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: Layers,
    },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForLandscapingContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
